import {
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@ibsys/kit';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { SaveButton } from '@/shared/ui';
import { useGenerateTitle } from '@/entities/content';
import {
  useKnowledgeSet,
  useCreateKnowledgeSet,
  useUpdateKnowledgeSet,
  useDeleteKnowledgeSet,
  type KnowledgeNodeInSet,
} from '@/entities/knowledge-set';
import { useAppNavigate } from '@/shared/lib';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { EditableKnowledgeSetTitle } from './EditableKnowledgeSetTitle';
import { SelectedNodesTab } from './SelectedNodesTab';
import { AllNodesTab } from './AllNodesTab';

type Props = {
  knowledgeSetId?: number;
  isCreating: boolean;
};

export const KnowledgeSetCard: FC<Props> = ({ knowledgeSetId, isCreating }) => {
  const { t } = useTranslation(['common', 'knowledgeSets']);
  const navigate = useAppNavigate();
  const { data, isLoading } = useKnowledgeSet(!isCreating ? knowledgeSetId : undefined);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [localNodeIds, setLocalNodeIds] = useState<number[]>([]);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const prevIdRef = useRef<number | undefined>(knowledgeSetId);
  const isNodesDirtyRef = useRef(false);
  const isNameSetByUserRef = useRef(false);

  useEffect(() => {
    if (isCreating) {
      setName('');
      setDescription('');
      setLocalNodeIds([]);
      isNodesDirtyRef.current = false;
      isNameSetByUserRef.current = false;
    } else if (data && prevIdRef.current !== knowledgeSetId) {
      setName(data.name);
      setDescription(data.description ?? '');
      setLocalNodeIds(data.nodeIds);
      prevIdRef.current = knowledgeSetId;
      isNodesDirtyRef.current = false;
    } else if (data) {
      setName(data.name);
      setDescription(data.description ?? '');
      if (!isNodesDirtyRef.current) {
        setLocalNodeIds(data.nodeIds);
      }
    }
  }, [data, isCreating, knowledgeSetId]);

  useEffect(() => {
    if (isCreating) {
      descriptionRef.current?.focus();
    }
  }, [isCreating]);

  const { mutate: generateTitle } = useGenerateTitle();

  const handleDescriptionBlur = useCallback(() => {
    if (!isCreating || isNameSetByUserRef.current) return;

    const trimmed = description.trim();
    if (trimmed && !name.trim()) {
      generateTitle(
        { text: trimmed, entityType: 'knowledge_set' },
        {
          onSuccess: (response) => {
            setName(response.title);
          },
        },
      );
    }
  }, [isCreating, description, name, generateTitle]);

  const { mutateAsync: createKnowledgeSet, isPending: isLoadingCreating } = useCreateKnowledgeSet();
  const { mutateAsync: updateKnowledgeSet, isPending: isLoadingUpdating } = useUpdateKnowledgeSet();
  const { mutateAsync: deleteKnowledgeSetMutation } = useDeleteKnowledgeSet();

  const isLoadingSaving = isLoadingCreating || isLoadingUpdating;

  const handleSave = useCallback(async () => {
    if (isCreating) {
      const { id } = await createKnowledgeSet({
        name,
        description: description || undefined,
        nodeIds: localNodeIds.length > 0 ? localNodeIds : undefined,
      });
      isNodesDirtyRef.current = false;
      navigate('/knowledge-sets/:id', { id: id.toString() });
    } else if (knowledgeSetId) {
      await updateKnowledgeSet({
        id: knowledgeSetId,
        payload: {
          name,
          description: description || undefined,
          nodeIds: localNodeIds,
        },
      });
      isNodesDirtyRef.current = false;
    }
  }, [isCreating, name, description, localNodeIds, knowledgeSetId, createKnowledgeSet, updateKnowledgeSet, navigate]);

  const handleDelete = useCallback(async () => {
    if (knowledgeSetId) {
      await deleteKnowledgeSetMutation(knowledgeSetId);
      navigate('/knowledge-sets');
    }
  }, [knowledgeSetId, deleteKnowledgeSetMutation, navigate]);

  const handleUpdateName = useCallback(
    async (newName: string) => {
      setName(newName);
      isNameSetByUserRef.current = true;
      if (knowledgeSetId && data) {
        await updateKnowledgeSet({
          id: knowledgeSetId,
          payload: {
            name: newName,
            description: data.description,
            nodeIds: localNodeIds,
          },
        });
      }
    },
    [knowledgeSetId, data, localNodeIds, updateKnowledgeSet],
  );

  const handleRemoveNode = useCallback((nodeId: number) => {
    isNodesDirtyRef.current = true;
    setLocalNodeIds((prev) => prev.filter((id) => id !== nodeId));
  }, []);

  const handleToggleNode = useCallback((nodeId: number, isInSet: boolean) => {
    isNodesDirtyRef.current = true;
    setLocalNodeIds((prev) => {
      if (isInSet) {
        return prev.includes(nodeId) ? prev : [...prev, nodeId];
      }
      return prev.filter((id) => id !== nodeId);
    });
  }, []);

  const nodesCacheRef = useRef(new Map<number, KnowledgeNodeInSet>());

  const handleNodesLoaded = useCallback((nodes: KnowledgeNodeInSet[]) => {
    for (const node of nodes) {
      nodesCacheRef.current.set(node.id, node);
    }
  }, []);

  const localNodeIdsSet = useMemo(() => new Set(localNodeIds), [localNodeIds]);

  const canEdit = useHasPermission('ADMIN_KNOWLEDGE');
  const isDeleted = data?.isDeleted ?? false;
  // Карточка доступна на редактирование только при праве администрирования и не для удалённого сета
  const isReadOnly = isDeleted || !canEdit;

  // Признак несохранённых изменений для индикации кнопки «Сохранить». При создании —
  // заполнено ли хоть что-то; при редактировании — описание или состав узлов отличаются
  // от сохранённых (название сохраняется отдельно, при редактировании заголовка).
  const isDirty = useMemo(() => {
    if (isCreating) {
      return name.trim() !== '' || description.trim() !== '' || localNodeIds.length > 0;
    }
    if (!data) return false;
    const original = data.nodeIds ?? [];
    const nodesChanged =
      original.length !== localNodeIds.length || localNodeIds.some((id) => !original.includes(id));
    return description !== (data.description ?? '') || nodesChanged;
  }, [isCreating, name, description, localNodeIds, data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="w-45 h-8" />
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full h-40" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden h-full flex flex-col">
      <CardHeader className="flex items-center gap-5 border-b">
        <EditableKnowledgeSetTitle name={name} onUpdateName={handleUpdateName} disabled={isReadOnly} />
        {!isReadOnly && (
          <SaveButton
            className="ml-auto"
            size="sm"
            isDirty={isDirty}
            isLoading={isLoadingSaving}
            onClick={handleSave}
            secondaryAction={
              !isCreating && (
                <ConfirmationButton
                  label={t('knowledgeSets:deleteKnowledgeSet')}
                  onConfirm={handleDelete}
                  title={t('knowledgeSets:deleteKnowledgeSetTitle')}
                  description={t('knowledgeSets:deleteKnowledgeSetDescription')}
                  trigger={{ size: 'sm' }}
                />
              )
            }
          />
        )}
      </CardHeader>
      <CardContent className="overflow-hidden flex-1 p-0 flex flex-col">
        <div className="flex flex-col gap-6 p-6 overflow-hidden flex-1">
          <Textarea
            ref={descriptionRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            label={t('knowledgeSets:description')}
            placeholder={t('knowledgeSets:descriptionPlaceholder')}
            rows={3}
            disabled={isReadOnly}
          />

          <Tabs defaultValue="selected" className="flex-1 flex flex-col overflow-hidden">
            <TabsList>
              <TabsTrigger value="selected">{t('knowledgeSets:selectedNodes')}</TabsTrigger>
              <TabsTrigger value="all">{t('knowledgeSets:allNodes')}</TabsTrigger>
            </TabsList>
            <TabsContent value="selected" className="flex-1 overflow-hidden">
              <SelectedNodesTab
                knowledgeSetId={knowledgeSetId}
                isCreating={isCreating}
                localNodeIds={localNodeIdsSet}
                nodesCache={nodesCacheRef.current}
                onRemoveNode={handleRemoveNode}
                readonly={isReadOnly}
              />
            </TabsContent>
            <TabsContent value="all" className="flex-1 overflow-hidden">
              <AllNodesTab
                knowledgeSetId={knowledgeSetId}
                isCreating={isCreating}
                localNodeIds={localNodeIdsSet}
                onToggleNode={handleToggleNode}
                onNodesLoaded={handleNodesLoaded}
                readonly={isReadOnly}
              />
            </TabsContent>
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
};
